import { CleanupFunction, DecisionContext, isLanguageChanged } from "../common/commonDomain";
import { identity } from "lodash";
import { DecisionType } from "../../common/decisions";
import {
  BooleanFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import { shareTypeLabel } from "../../common/share";
import { LocalizedText } from "../../common/common";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";

export enum ShareCapitalDuration {
  untilGM = "untilGM",
  months = "months",
}

export type IncreaseShareCapitalPowerOfAttorney = {
  type: DecisionType.increaseShareCapitalPowerOfAttorney;
  shareCapitalValue: number;
  shareCapitalDuration: ShareCapitalDuration;
  shareCapitalDurationMonths?: number;
  deviateFromShareholderRights: boolean;
  shareTypes: string[];
  isAllShareTypes: boolean;
  isNewShareType: boolean;
  newShareTypeDescription: string;
  includeOtherProperties: boolean;
  includeMerger: boolean;
  includeConvertingDebt: boolean;
  shareCapitalReasons: LocalizedText[];
};

export class IncreaseShareCapitalPowerOfAttorneyForm {
  readonly type = DecisionType.increaseShareCapitalPowerOfAttorney;
  context: DecisionContext;
  data?: IncreaseShareCapitalPowerOfAttorney;
  shareCapitalValue: NumberFieldModel;
  shareCapitalDuration: RadioFieldModel<ShareCapitalDuration>;
  shareCapitalDurationMonths: NumberFieldModel;
  deviateFromShareholderRights: BooleanFieldModel;
  isNewShareType: BooleanFieldModel;
  isAllShareTypes: BooleanFieldModel;
  shareTypes: OptionFieldModel<string>;
  newShareTypeDescription: StringFieldModel;
  includeOtherProperties: BooleanFieldModel;
  includeMerger: BooleanFieldModel;
  includeConvertingDebt: BooleanFieldModel;
  shareCapitalReasons: LocalizedTextEditorModel;

  constructor(context: DecisionContext, data?: IncreaseShareCapitalPowerOfAttorney) {
    this.context = context;
    this.data = data;
    this.shareCapitalValue = new NumberFieldModel({
      value: data?.shareCapitalValue,
      isInteger: true,
      isPositive: true,
      required: true,
    });
    this.shareCapitalDuration = new RadioFieldModel<ShareCapitalDuration>({
      value: data?.shareCapitalDuration ?? ShareCapitalDuration.untilGM,
      options: Object.values(ShareCapitalDuration),
      idFn: identity,
      labelFn: (v) => ({ key: `decisions.increaseShareCapitalPoA.shareCapitalDuration.${v}` }),
    });
    this.shareCapitalDurationMonths = new NumberFieldModel({
      value: data?.shareCapitalDurationMonths,
      isPositive: true,
      isInteger: true,
    });
    this.isNewShareType = new BooleanFieldModel({
      value: data?.isNewShareType,
    });
    this.isAllShareTypes = new BooleanFieldModel({
      value: data?.isAllShareTypes ?? false,
    });
    this.shareTypes = new OptionFieldModel({
      value: data?.shareTypes ?? this.allShareTypeNames,
      options: this.allShareTypeNames,
      idFn: identity,
      labelFn: shareTypeLabel,
    });
    this.newShareTypeDescription = new StringFieldModel({ value: data?.newShareTypeDescription });
    this.deviateFromShareholderRights = new BooleanFieldModel({
      value: data?.deviateFromShareholderRights,
    });
    this.includeOtherProperties = new BooleanFieldModel({
      value: data?.includeOtherProperties,
    });
    this.includeMerger = new BooleanFieldModel({
      value: data?.includeMerger,
    });
    this.includeConvertingDebt = new BooleanFieldModel({
      value: data?.includeConvertingDebt,
    });

    this.shareCapitalReasons = new LocalizedTextEditorModel({
      texts: data?.shareCapitalReasons.map((x) => ({
        ...x,
        translationOption: { autoTranslate: false },
      })),
      aiClient: context.aiClient,
      isReadOnly: () => context.isLocked,
      label: { key: "decisions.increaseShareCapitalPoA.shareCapitalReason" },
    });

    makeAutoObservable(this, { type: false }, { autoBind: true });
  }

  init(): CleanupFunction {
    const cleanupFunctions = [
      autorun(() => {
        this.reactToLanguageChanges();
      }),
    ];
    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }

  reactToLanguageChanges() {
    runInAction(() => {
      if (isLanguageChanged(this.context.documentLanguages, this.shareCapitalReasons.textModels)) {
        this.shareCapitalReasons.onLanguageChange(this.context.documentLanguages, undefined, {
          autoTranslate: false,
        });
      }
    });
  }

  setDurationMonthsLimit() {
    if (
      this.shareCapitalDurationMonths.parsedValue &&
      this.shareCapitalDurationMonths.parsedValue > 24
    ) {
      this.shareCapitalDurationMonths.setAndCommitInputValue(24);
    }
  }

  validate(): boolean {
    this.shareCapitalValue.validate();
    this.shareCapitalDurationMonths.validate();
    this.deviateFromShareholderRights.validate();
    this.isNewShareType.validate();
    this.isAllShareTypes.validate();
    this.newShareTypeDescription.validate();
    this.includeOtherProperties.validate();
    this.includeMerger.validate();
    this.includeConvertingDebt.validate();
    this.shareCapitalReasons.validate();
    return this.isValid;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.shareCapitalValue.isValid &&
      this.shareCapitalDurationMonths.isValid &&
      this.deviateFromShareholderRights.isValid &&
      this.isNewShareType.isValid &&
      this.isAllShareTypes.isValid &&
      this.newShareTypeDescription.isValid &&
      this.includeOtherProperties.isValid &&
      this.includeMerger.isValid &&
      this.includeConvertingDebt.isValid &&
      this.shareCapitalReasons.isValid
    );
  }

  get isDurationMonths(): boolean {
    return this.shareCapitalDuration.value === ShareCapitalDuration.months;
  }

  get allShareTypeNames(): string[] {
    return this.context.companyInfo.shareTypes.map((s) => s.sharetypeCode) ?? [];
  }

  get showNewShareTypeDescriptionInput() {
    return this.isNewShareType.value;
  }
}
